import { useState } from 'react';
import { CartItem } from 'modules/cart/types/cart';
import { OptimizedCartResponse, OptimizedCartStatusResponse } from 'modules/checkout/types/optimizedCart';
import { endPointRecalcOptimizedResults, endPointCheckStatus, endPointOptimizedResults } from 'modules/main/services/endpoints';

interface UseOptimizedCartUpdatesProps {
    orderId: number;
    selectedIndexScenario: number;
    onSuccess?: (updatedData: OptimizedCartResponse) => void;
    onError?: (error: Error) => void;
}

export const useOptimizedCartUpdates = ({
    orderId,
    selectedIndexScenario,
    onSuccess,
    onError
}: UseOptimizedCartUpdatesProps) => {
    const [isApplyingChanges, setIsApplyingChanges] = useState(false);

    const applyChanges = async (scenarioId:number,workingCart: CartItem[]) => {
        if (isApplyingChanges) return;
        
        setIsApplyingChanges(true);
        
        try {
            const updateRequest = {
                products: workingCart
            };
            
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 120000);
            
            // 1. Enviar solicitud de recálculo
            const response = await fetch(endPointRecalcOptimizedResults(scenarioId), {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json"
                },
                credentials: "include",
                body: JSON.stringify(updateRequest),
                signal: controller.signal
            });
            
            clearTimeout(timeoutId);
            console.log("response", response);
            
            
            if (!response.ok) {
                // Intentar obtener el body del error para más detalles
                let errorData;
                try {
                    const errorText = await response.text();
                    console.log("❌ Error Response Body (raw):", errorText);
                    errorData = JSON.parse(errorText);
                    console.log("❌ Error Response Body (parsed):", errorData);
                } catch (parseError) {
                    console.log("❌ Could not parse error response as JSON");
                    errorData = { message: "Error desconocido" };
                }
                
                throw new Error(`HTTP ${response.status}: ${errorData.message || errorData.detail || response.statusText}`);
            }
            
            const recalcResponse = await response.json();
            
            
            // 2. Comenzar polling para verificar el estado
            const pollForResults = async (): Promise<OptimizedCartResponse> => {
                return new Promise((resolve, reject) => {
                    const checkStatus = async () => {
                        try {
                            const statusResponse = await fetch(endPointCheckStatus(orderId.toString()), {
                                method: "GET",
                                credentials: "include",
                                headers: {"content-type": "application/json"}
                            });
                            
                            const statusResult = await statusResponse.json() as OptimizedCartStatusResponse;
                            
                            if (statusResult.status === "CO") {
                                // 3. Una vez completado, obtener los resultados
                                const resultsResponse = await fetch(endPointOptimizedResults(orderId), {
                                    method: "GET",
                                    credentials: "include",
                                    headers: {"content-type": "application/json"}
                                });
                                
                                if (!resultsResponse.ok) {
                                    throw new Error(`Error al obtener resultados: ${resultsResponse.status}`);
                                }
                                
                                const updatedData = await resultsResponse.json() as OptimizedCartResponse;
                                
                                if (!updatedData.scenarios?.[selectedIndexScenario]) {
                                    throw new Error("Respuesta del servidor inválida: falta escenario seleccionado");
                                }
                                
                                clearInterval(interval);
                                resolve(updatedData);
                                return;
                            }
                            
                            if (statusResult.status === "CN") {
                                clearInterval(interval);
                                reject(new Error(statusResult.error ?? "Error en el recálculo"));
                                return;
                            }
                            
                            // Si está en proceso (PR o AC), continuar polling
                            // console.log("Recálculo en proceso, estado:", statusResult.status);
                            
                        } catch (error) {
                            clearInterval(interval);
                            reject(error);
                        }
                    };
                    
                    // Polling cada 3 segundos
                    const interval = setInterval(checkStatus, 3000);
                    
                    // Timeout general de 2 minutos para todo el proceso
                    setTimeout(() => {
                        clearInterval(interval);
                        reject(new Error("Timeout: El recálculo tardó demasiado tiempo"));
                    }, 180000); // 3 minutos
                });
            };
            
            // 4. Esperar a que el polling complete
            const updatedData = await pollForResults();
            
            onSuccess?.(updatedData);
            return updatedData;
            
        } catch (error) {
            console.error("Error al aplicar cambios:", error);
            
            // onError?.(error as Error);
            // throw error;
            
        } finally {
            setIsApplyingChanges(false);
        }
    };

    return {
        applyChanges,
        isApplyingChanges
    };
};